import { useState, useCallback, useEffect } from 'react';
import { Phone, Users, Clock, Wifi, WifiOff, MessageSquare, Radio } from 'lucide-react';
import { useVoice } from '@/context/VoiceContext';
import { toast } from 'sonner';
import api from '@/lib/api';
import DialPad from '@/components/voice/DialPad';
import ContactsPanel from '@/components/voice/ContactsPanel';
import RecentsPanel from '@/components/voice/RecentsPanel';
import ActiveCallPanel from '@/components/voice/ActiveCallPanel';
import MessagingChat from '@/components/voice/MessagingChat';
import MessagingConversations from '@/components/voice/MessagingConversations';

const MOBILE_TABS_CALL = [
  { id: 'dialpad', label: 'Keypad', icon: Phone },
  { id: 'contacts', label: 'Contacts', icon: Users },
  { id: 'recents', label: 'Recent', icon: Clock },
];

const MOBILE_TABS_TEXT = [
  { id: 'chat', label: 'Chat', icon: MessageSquare },
  { id: 'contacts', label: 'Contacts', icon: Users },
  { id: 'conversations', label: 'Threads', icon: Clock },
];

export default function PhonePage() {
  const { makeCall, device, callStatus, isDeviceReady, incomingCall } = useVoice();
  const [dialNumber, setDialNumber] = useState('');
  const [mode, setMode] = useState('call'); // 'call' | 'text'
  const [mobileTab, setMobileTab] = useState('dialpad');
  const [recentsRefresh, setRecentsRefresh] = useState(0);

  // SMS state
  const [smsStatus, setSmsStatus] = useState({ configured: false, test_mode: false, from: '' });
  const [activeThread, setActiveThread] = useState(null); // { number, contact_name }
  const [conversationsRefresh, setConversationsRefresh] = useState(0);
  const [manualNumber, setManualNumber] = useState('');
  const [recording, setRecording] = useState({ enabled: false, announce: true });
  const [contactsRefresh, setContactsRefresh] = useState(0);

  useEffect(() => {
    api.get('/voice/recording-settings').then(r => setRecording(r.data)).catch(() => {});
  }, []);

  const isOnCall = callStatus !== 'idle' || !!incomingCall;
  const mobileTabs = mode === 'text' ? MOBILE_TABS_TEXT : MOBILE_TABS_CALL;

  useEffect(() => {
    api.get('/voice/sms/status').then(r => setSmsStatus(r.data)).catch(() => {});
  }, []);

  // Reset mobile tab when switching mode
  useEffect(() => {
    setMobileTab(mode === 'text' ? 'chat' : 'dialpad');
  }, [mode]);

  const handleCall = useCallback(async (number, displayName) => {
    if (!device) {
      toast.error('Voice service not available. Configure Twilio credentials in Settings.');
      return;
    }
    if (callStatus !== 'idle') {
      toast.warning('Already on a call');
      return;
    }
    try {
      await makeCall(number, displayName || number);
      setDialNumber('');
      setRecentsRefresh(r => r + 1);
    } catch (err) {
      toast.error(`Call failed: ${err.message}`);
    }
  }, [device, callStatus, makeCall]);

  const handleDialTo = useCallback((number) => {
    setDialNumber(number);
    setMobileTab('dialpad');
  }, []);

  const handleSelectThread = useCallback((thread) => {
    setActiveThread(thread);
    setMobileTab('chat');
  }, []);

  const handleOpenManualThread = (e) => {
    e.preventDefault();
    const n = manualNumber.trim();
    if (!n) return;
    setActiveThread({ number: n, contact_name: '' });
    setMobileTab('chat');
  };

  const handleSmsSent = () => {
    setConversationsRefresh(r => r + 1);
  };

  const handleContactChange = useCallback((updatedContact) => {
    setContactsRefresh(r => r + 1);
    if (updatedContact) {
      const name = `${updatedContact.first_name || ''} ${updatedContact.last_name || ''}`.trim();
      setActiveThread(prev => (prev ? { ...prev, contact_id: updatedContact.id, contact_name: name } : prev));
    }
  }, []);

  // ─── Desktop layout ───────────────────────────────────────────────────────
  return (
    <div className="flex flex-col h-[calc(100vh-4rem-3rem)] min-h-[600px]">
      {/* Page header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Phone</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            {mode === 'text'
              ? 'Send/receive SMS, view conversations'
              : 'Call, manage contacts, and view history'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {mode === 'call' && recording.enabled && (
            <span
              data-testid="phone-recording-badge"
              title={recording.announce
                ? 'Calls are recorded. Both parties hear a recording notice before connecting.'
                : 'Calls are recorded.'}
              className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-red-50 text-red-600 border border-red-200"
            >
              <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
              <Radio className="w-3.5 h-3.5" /> Recording on
            </span>
          )}
          {/* Mode toggle */}
          <div className="flex bg-slate-100 rounded-xl p-1" data-testid="phone-mode-toggle">
            <button
              data-testid="phone-mode-call"
              onClick={() => setMode('call')}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition ${mode === 'call' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            >
              <Phone className="w-3.5 h-3.5" /> Call
            </button>
            <button
              data-testid="phone-mode-text"
              onClick={() => setMode('text')}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition ${mode === 'text' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            >
              <MessageSquare className="w-3.5 h-3.5" /> Text
            </button>
          </div>
          <span className={`
            flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full
            ${mode === 'call'
              ? (isDeviceReady
                ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                : 'bg-slate-100 text-slate-500 border border-slate-200')
              : (smsStatus.configured
                ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                : 'bg-slate-100 text-slate-500 border border-slate-200')
            }
          `}>
            {mode === 'call'
              ? (isDeviceReady
                ? <><Wifi className="w-3.5 h-3.5" /> Ready</>
                : <><WifiOff className="w-3.5 h-3.5" /> {device ? 'Initializing...' : 'Not configured'}</>)
              : (smsStatus.configured
                ? <><Wifi className="w-3.5 h-3.5" /> SMS Ready{smsStatus.test_mode ? ' · Test' : ''}</>
                : <><WifiOff className="w-3.5 h-3.5" /> SMS not configured</>)
            }
          </span>
        </div>
      </div>

      {/* ─── DESKTOP: 3-column grid ─── */}
      <div className="hidden lg:grid grid-cols-[260px_1fr_300px] gap-4 flex-1 min-h-0">

        {/* LEFT: Contacts */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm flex flex-col min-h-0 overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2 flex-shrink-0">
            <Users className="w-4 h-4 text-slate-400" />
            <span className="text-sm font-semibold text-slate-700">Contacts</span>
          </div>
          <div className="flex-1 min-h-0 overflow-hidden">
            <ContactsPanel
              onDialTo={handleDialTo}
              mode={mode}
              onText={handleSelectThread}
              refreshTrigger={contactsRefresh}
            />
          </div>
        </div>

        {/* CENTER: Dialpad / Active Call / Chat */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm flex flex-col min-h-0 overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2 flex-shrink-0">
            {mode === 'text'
              ? <MessageSquare className="w-4 h-4 text-slate-400" />
              : <Phone className="w-4 h-4 text-slate-400" />}
            <span className="text-sm font-semibold text-slate-700">
              {mode === 'text' ? 'Text Message' : (isOnCall ? 'Active Call' : 'Dial')}
            </span>
          </div>
          <div className="flex-1 min-h-0 flex flex-col">
            {mode === 'text' ? (
              <MessagingChat
                number={activeThread?.number}
                contactName={activeThread?.contact_name}
                smsStatus={smsStatus}
                onSent={handleSmsSent}
                onContactChange={handleContactChange}
              />
            ) : isOnCall ? (
              <div className="flex-1 p-4">
                <ActiveCallPanel />
              </div>
            ) : (
              <div className="flex-1 flex items-center justify-center p-6">
                <DialPad
                  number={dialNumber}
                  setNumber={setDialNumber}
                  onCall={handleCall}
                  disabled={!device || callStatus !== 'idle'}
                />
              </div>
            )}
          </div>
        </div>

        {/* RIGHT: Recent Calls / SMS Conversations */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm flex flex-col min-h-0 overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2 flex-shrink-0">
            <Clock className="w-4 h-4 text-slate-400" />
            <span className="text-sm font-semibold text-slate-700">
              {mode === 'text' ? 'Conversations' : 'Recent Calls'}
            </span>
          </div>
          {mode === 'text' && (
            <form onSubmit={handleOpenManualThread} className="p-2 border-b border-slate-100 flex gap-1.5">
              <input
                data-testid="sms-manual-number"
                value={manualNumber}
                onChange={e => setManualNumber(e.target.value)}
                placeholder="+1..."
                className="flex-1 text-xs px-2 py-1.5 border border-slate-200 rounded-lg focus:outline-none focus:border-[#003366]/40"
              />
              <button type="submit" className="text-xs px-2 py-1.5 rounded-lg bg-[#003366] text-white hover:bg-[#004080]" data-testid="sms-open-thread-btn">Open</button>
            </form>
          )}
          <div className="flex-1 min-h-0 overflow-y-auto">
            {mode === 'text'
              ? <MessagingConversations activeNumber={activeThread?.number} onSelect={handleSelectThread} refreshKey={conversationsRefresh} />
              : <RecentsPanel refreshTrigger={recentsRefresh} />}
          </div>
        </div>
      </div>

      {/* ─── MOBILE: Tab-based layout ─── */}
      <div className="lg:hidden flex flex-col flex-1 min-h-0">

        {isOnCall && mode === 'call' && (
          <div className="mb-3 rounded-2xl overflow-hidden border border-slate-200 shadow-sm h-[340px] flex-shrink-0">
            <ActiveCallPanel />
          </div>
        )}

        <div className="flex bg-slate-100 rounded-xl p-1 mb-3 flex-shrink-0">
          {mobileTabs.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              data-testid={`phone-mobile-tab-${id}`}
              onClick={() => setMobileTab(id)}
              className={`
                flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-xs font-semibold
                transition-all duration-150
                ${mobileTab === id
                  ? 'bg-white text-slate-800 shadow-sm'
                  : 'text-slate-500 hover:text-slate-700'
                }
              `}
            >
              <Icon className="w-3.5 h-3.5" />
              {label}
            </button>
          ))}
        </div>

        <div className="flex-1 min-h-0 bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          {mode === 'call' && mobileTab === 'dialpad' && (
            <div className="flex items-center justify-center h-full p-6">
              <DialPad
                number={dialNumber}
                setNumber={setDialNumber}
                onCall={handleCall}
                disabled={!device || callStatus !== 'idle'}
              />
            </div>
          )}
          {mobileTab === 'contacts' && (
            <ContactsPanel onDialTo={handleDialTo} mode={mode} onText={handleSelectThread} refreshTrigger={contactsRefresh} />
          )}
          {mode === 'call' && mobileTab === 'recents' && (
            <RecentsPanel refreshTrigger={recentsRefresh} />
          )}
          {mode === 'text' && mobileTab === 'chat' && (
            <MessagingChat
              number={activeThread?.number}
              contactName={activeThread?.contact_name}
              smsStatus={smsStatus}
              onSent={handleSmsSent}
              onContactChange={handleContactChange}
            />
          )}
          {mode === 'text' && mobileTab === 'conversations' && (
            <MessagingConversations activeNumber={activeThread?.number} onSelect={handleSelectThread} refreshKey={conversationsRefresh} />
          )}
        </div>
      </div>
    </div>
  );
}
